/** Parts List (reference/Part_Sheet_20.pdf). Office/Manager enter prices; extended is the server's qty x sell. */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorText } from '../../lib/api';
import { date, dateTime } from '../../lib/format';
import type { PartLine, PartsList } from '../../lib/form-types';
import { Button, Card, ErrorText, Field, Input, Loading, Modal } from '../../components/ui';

const blankLine = { qty: '1', partNo: '', replacedPartNo: '', description: '', stock: false, cost: '', list: '', sell: '' };
const money = (v: string | null | undefined) => (v ? `$${v}` : '—');

export function PartsTab({ woId, readOnly, onChange }: { woId: number; readOnly: boolean; onChange: () => void }) {
  const qc = useQueryClient();
  const key = ['wo-parts', woId];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<PartsList>(`/api/work-orders/${woId}/parts`) });
  const [add, setAdd] = useState(blankLine);
  const [editing, setEditing] = useState<PartLine | null>(null);
  const [removing, setRemoving] = useState<PartLine | null>(null);
  const [error, setError] = useState<string | null>(null);

  const done = (data: PartsList) => {
    qc.setQueryData(key, data);
    onChange();
  };
  const run = async (fn: () => Promise<PartsList>) => {
    setError(null);
    try {
      done(await fn());
      return true;
    } catch (e) {
      setError(errorText(e));
      return false;
    }
  };

  if (q.isLoading || !q.data) return <Loading />;
  const p = q.data;
  const h = p.header!;
  const active = p.lines.filter((l) => !l.removed);

  return (
    <div className="space-y-4">
      <ErrorText error={error} />
      <Header woId={woId} header={h} readOnly={readOnly} onSaved={done} />
      <Card
        title={`Parts (${active.length})`}
        actions={
          !readOnly && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={p.noPartsUsed} disabled={active.length > 0} onChange={(e) => run(() => api.post(`/api/work-orders/${woId}/parts/no-parts-used`, { value: e.target.checked }))} />
              No parts used
            </label>
          )
        }
      >
        {p.noPartsUsed && <p className="mb-2 rounded bg-green-50 p-2 text-sm text-green-900">No parts used{p.noPartsUsedAt && ` (ticked ${dateTime(p.noPartsUsedAt)})`}.</p>}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b text-xs uppercase text-slate-500">
              <tr>
                <th className="py-1">Qty</th><th>Part No.</th><th># Replaced</th><th>Description</th><th>Stock</th>
                <th className="text-right">Cost</th><th className="text-right">List</th><th className="text-right">Sell</th><th className="text-right">Extended</th><th /><th />
              </tr>
            </thead>
            <tbody className="divide-y">
              {p.lines.map((l) => (
                <tr key={l.id} className={l.removed ? 'text-slate-400 line-through' : ''}>
                  <td className="py-1.5">{Number(l.qty)}</td>
                  <td>{l.partNo ?? '—'}</td>
                  <td>{l.replacedPartNo ?? '—'}</td>
                  <td className="no-underline">
                    {l.description}
                    <div className="text-xs text-slate-500" style={{ textDecoration: 'none' }}>
                      {l.source === 'MECHANIC' ? 'mechanic' : 'office'} · {dateTime(l.createdAt)}
                      {l.lateAdd && <span className="ml-1 rounded bg-amber-200 px-1 text-amber-900">Late add</span>}
                      {l.removed && <span className="ml-1">removed: “{l.removedReason}”</span>}
                    </div>
                  </td>
                  <td>{l.stock ? 'Yes' : ''}</td>
                  <td className="text-right">{money(l.cost)}</td>
                  <td className="text-right">{money(l.list)}</td>
                  <td className="text-right">{money(l.sell)}</td>
                  <td className="text-right font-semibold">{money(l.extended)}</td>
                  <td>{!readOnly && !l.removed && <Button variant="ghost" onClick={() => setEditing(l)}>Edit</Button>}</td>
                  <td>{!readOnly && !l.removed && <Button variant="ghost" onClick={() => setRemoving(l)}>Remove</Button>}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t font-semibold"><td colSpan={8} className="py-2 text-right">Total</td><td className="text-right">${p.total}</td><td colSpan={2} /></tr>
            </tfoot>
          </table>
        </div>
        {p.lines.length === 0 && <p className="py-3 text-sm text-slate-500">No parts yet.</p>}
        {!readOnly && (
          <div className="mt-4 grid grid-cols-2 items-end gap-2 border-t pt-3 md:grid-cols-9">
            <Field label="Qty"><Input value={add.qty} inputMode="decimal" onChange={(e) => setAdd({ ...add, qty: e.target.value })} /></Field>
            <Field label="Part No."><Input value={add.partNo} onChange={(e) => setAdd({ ...add, partNo: e.target.value })} /></Field>
            <Field label="# Replaced"><Input value={add.replacedPartNo} onChange={(e) => setAdd({ ...add, replacedPartNo: e.target.value })} /></Field>
            <div className="col-span-2"><Field label="Description"><Input value={add.description} onChange={(e) => setAdd({ ...add, description: e.target.value })} /></Field></div>
            <Field label="Cost"><Input value={add.cost} inputMode="decimal" onChange={(e) => setAdd({ ...add, cost: e.target.value })} /></Field>
            <Field label="List"><Input value={add.list} inputMode="decimal" onChange={(e) => setAdd({ ...add, list: e.target.value })} /></Field>
            <Field label="Sell"><Input value={add.sell} inputMode="decimal" onChange={(e) => setAdd({ ...add, sell: e.target.value })} /></Field>
            <Button
              disabled={!add.description.trim() || !add.qty}
              onClick={async () => {
                if (await run(() => api.post(`/api/work-orders/${woId}/parts`, add))) setAdd(blankLine);
              }}
            >
              Add line
            </Button>
          </div>
        )}
        <p className="mt-2 text-xs text-slate-500">Extended = Qty × Sell, worked out by the server. Lines are never deleted — removed lines stay listed with the reason.</p>
      </Card>
      {editing && <EditLine line={editing} onClose={() => setEditing(null)} onSave={async (b) => (await run(() => api.patch(`/api/parts-lines/${editing.id}`, b))) && setEditing(null)} />}
      {removing && <RemoveLine line={removing} onClose={() => setRemoving(null)} onSave={async (reason) => (await run(() => api.post(`/api/parts-lines/${removing.id}/remove`, { reason }))) && setRemoving(null)} />}
    </div>
  );
}

function Header({ woId, header, readOnly, onSaved }: { woId: number; header: NonNullable<PartsList['header']>; readOnly: boolean; onSaved: (p: PartsList) => void }) {
  const [f, setF] = useState({ purchaseOrder: header.purchaseOrder ?? '', salesRep: header.salesRep ?? '', customerPo: header.customerPo ?? '', contactName: header.contactName ?? '', contactPhone: header.contactPhone ?? '' });
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <Card title="Parts List" actions={!readOnly && <Button variant="secondary" onClick={async () => {
      try { onSaved(await api.put(`/api/work-orders/${woId}/parts/header`, f)); } catch (e) { setError(errorText(e)); }
    }}>Save header</Button>}>
      <ErrorText error={error} />
      <div className="grid gap-3 text-sm md:grid-cols-4">
        <div><span className="text-slate-500">Date</span><div>{date(header.date)}</div></div>
        <div><span className="text-slate-500">S/O</span><div className="font-semibold">{header.so}</div></div>
        <Field label="P/O"><Input value={f.purchaseOrder} onChange={set('purchaseOrder')} disabled={readOnly} /></Field>
        <Field label="Sales rep"><Input value={f.salesRep} onChange={set('salesRep')} disabled={readOnly} /></Field>
        <Field label="Customer's P/O"><Input value={f.customerPo} onChange={set('customerPo')} disabled={readOnly} /></Field>
        <div><span className="text-slate-500">Customer name</span><div>{header.customerName}</div></div>
        <Field label="Contact"><Input value={f.contactName} onChange={set('contactName')} disabled={readOnly} /></Field>
        <Field label="Phone / Fax"><Input value={f.contactPhone} onChange={set('contactPhone')} disabled={readOnly} /></Field>
        <div><span className="text-slate-500">Make / Model</span><div>{header.makeModel}</div></div>
        <div><span className="text-slate-500">Serial number</span><div>{header.serialNumber}</div></div>
      </div>
    </Card>
  );
}

function EditLine({ line, onClose, onSave }: { line: PartLine; onClose: () => void; onSave: (b: object) => void }) {
  const [f, setF] = useState({ qty: line.qty, partNo: line.partNo ?? '', replacedPartNo: line.replacedPartNo ?? '', description: line.description, stock: line.stock ?? false, cost: line.cost ?? '', list: line.list ?? '', sell: line.sell ?? '' });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal title="Edit part line" onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => onSave(f)}>Save</Button></>}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Qty"><Input value={f.qty} onChange={set('qty')} inputMode="decimal" /></Field>
        <Field label="Part No."><Input value={f.partNo} onChange={set('partNo')} /></Field>
        <Field label="# Replaced"><Input value={f.replacedPartNo} onChange={set('replacedPartNo')} /></Field>
        <label className="mt-6 flex items-center gap-2 text-sm"><input type="checkbox" checked={f.stock} onChange={(e) => setF({ ...f, stock: e.target.checked })} /> Stock</label>
      </div>
      <Field label="Description"><Input value={f.description} onChange={set('description')} /></Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Cost"><Input value={f.cost} onChange={set('cost')} inputMode="decimal" /></Field>
        <Field label="List"><Input value={f.list} onChange={set('list')} inputMode="decimal" /></Field>
        <Field label="Sell"><Input value={f.sell} onChange={set('sell')} inputMode="decimal" /></Field>
      </div>
      <p className="text-xs text-slate-500">The change is recorded in the audit log (before and after).</p>
    </Modal>
  );
}

function RemoveLine({ line, onClose, onSave }: { line: PartLine; onClose: () => void; onSave: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal title={`Remove “${line.description}”`} onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button variant="danger" disabled={reason.trim().length < 3} onClick={() => onSave(reason)}>Remove line</Button></>}>
      <p className="text-sm text-slate-600">The line stays on the list, struck through, with your reason.</p>
      <Field label="Reason" required><Input value={reason} onChange={(e) => setReason(e.target.value)} autoFocus /></Field>
    </Modal>
  );
}
